import { httpClient } from '../client';
import { WorkingHour } from '../../../types/workingHours';
import { logger } from '../../../utils/logger';
import { BaseEndpoint } from './base.endpoint';

class WorkingHoursEndpoints extends BaseEndpoint {
  private static instance: WorkingHoursEndpoints;

  private constructor() {
    super('organisations');
  }

  static getInstance(): WorkingHoursEndpoints {
    if (!WorkingHoursEndpoints.instance) {
      WorkingHoursEndpoints.instance = new WorkingHoursEndpoints();
    }
    return WorkingHoursEndpoints.instance;
  }

  async getWorkingHours(organisationId: string) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get(`${this.basePath}/${organisationId}/workinghours`, {
        headers,
      });
      logger.info('Working hours fetched successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch working hours:', error);
      throw error;
    }
  }

  async deleteWorkingHour(organisationId: string, workinghourId: number) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.delete(
        `${this.basePath}/${organisationId}/workinghours/${workinghourId}`,
        {
          headers,
        }
      );
      logger.info('Working hour deleted successfully:', workinghourId);
    } catch (error) {
      logger.error('Failed to delete working hour:', error);
      throw error;
    }
  }

  async updateWorkingHours(organisationId: string, workingHours: WorkingHour[]) {
    try {
      const headers = await this.getAuthHeaders();
      const promises = workingHours.map(workingHour =>
        httpClient.put(
          `${this.basePath}/${organisationId}/workinghours/${workingHour.id}`,
          {
            reosurceId: workingHour.resourceId ? workingHour.resourceId : null,
            day: workingHour.day?.toLowerCase(),
            startTime: workingHour.startTime,
            endTime: workingHour.endTime,
          },
          { headers }
        )
      );
      await Promise.all(promises);
      logger.info('Working hours updated successfully');
      return workingHours;
    } catch (error) {
      logger.error('Failed to update working hours:', error);
      throw error;
    }
  }

  async deleteAllWorkingHours(organisationId: string, workingHours: WorkingHour[]) {
    try {
      const promises = workingHours.map(wh =>
        this.deleteWorkingHour(organisationId, Number(wh.id))
      );
      await Promise.all(promises);
      logger.info('All working hours deleted successfully');
    } catch (error) {
      logger.error('Failed to delete working hours:', error);
      throw error;
    }
  }

  async createWorkingHours(organisationId: string, workingHour: Partial<WorkingHour>) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post(
        `${this.basePath}/${organisationId}/workinghours`,
        {
          resourceId: workingHour.resourceId,
          day: workingHour.day?.toLowerCase(),
          startTime: workingHour.startTime,
          endTime: workingHour.endTime,
        },
        { headers }
      );
      logger.info('Working hours created successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to create working hours:', error);
      throw error;
    }
  }

  async createMultipleWorkingHours(organisationId: string, workingHours: Partial<WorkingHour>[]) {
    try {
      const promises = workingHours.map(workingHour =>
        this.createWorkingHours(organisationId, workingHour)
      );
      const results = await Promise.all(promises);
      logger.info('All working hours created successfully');
      return results;
    } catch (error) {
      logger.error('Failed to create working hours:', error);
      throw error;
    }
  }
}

export const workingHoursEndpoints = WorkingHoursEndpoints.getInstance();
